import type {
  OrderFilters as OrderFiltersType,
  OrderTypeFilter,
} from '@/api/orders/orderTypes';
import {
  DateRangePickerButton,
  FilterOptionButton,
} from '@/components/common/FilterButtons';
import { Input } from '@/components/common/Input';
import type { OrderState } from '@/domain/Order';
import { useEffect, useState } from 'react';

const STATUS_FILTERS: Array<{ label: string; value: OrderState | 'All' }> = [
  { label: 'All', value: 'All' },
  { label: 'New orders', value: 'Paid' },
  { label: 'Waiting', value: 'Waiting' },
  { label: 'Approved', value: 'Approved' },
  { label: 'Rejected', value: 'Rejected' },
  { label: 'Shipped', value: 'Shipped' },
  { label: 'In terminal', value: 'In terminal' },
  { label: 'Delivered', value: 'Delivered' },
  { label: 'Failed', value: 'Failed' },
];

type TimeFilter =
  | 'This week'
  | 'Last week'
  | 'This month'
  | 'Last month'
  | 'Paid'
  | 'Custom';

const TIME_FILTERS: Array<{
  label: React.ReactNode;
  value: TimeFilter;
}> = [
  { label: 'This week', value: 'This week' },
  { label: 'Last week', value: 'Last week' },
  { label: 'This month', value: 'This month' },
  { label: 'Last month', value: 'Last month' },
  { label: '🚩', value: 'Paid' },
];

const ORDER_TYPE_FILTERS: Array<{
  label: string;
  value: OrderTypeFilter | 'All';
}> = [
  { label: 'All', value: 'All' },
  { label: 'Parent order', value: 'parent' },
  { label: 'Child order', value: 'child' },
];

interface OrderFiltersProps {
  filters: OrderFiltersType;
  onFiltersChange: (filters: OrderFiltersType) => void;
  status?: OrderState;
}

// Format date as YYYY-MM-DD using local time (avoids UTC conversion issues)
const formatDateLocal = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getDateRange = (
  filter: TimeFilter,
): { date_from?: string; date_to?: string } => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (filter) {
    case 'This week': {
      const dayOfWeek = today.getDay();
      const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() + diffToMonday);
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      return {
        date_from: formatDateLocal(startOfWeek),
        date_to: formatDateLocal(endOfWeek),
      };
    }
    case 'Last week': {
      // Get Monday of last week
      const dayOfWeek = today.getDay();
      const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      const thisMonday = new Date(today);
      thisMonday.setDate(today.getDate() + diffToMonday);
      const startOfLastWeek = new Date(thisMonday);
      startOfLastWeek.setDate(thisMonday.getDate() - 7);
      const endOfLastWeek = new Date(startOfLastWeek);
      endOfLastWeek.setDate(startOfLastWeek.getDate() + 6);
      return {
        date_from: formatDateLocal(startOfLastWeek),
        date_to: formatDateLocal(endOfLastWeek),
      };
    }
    case 'This month': {
      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      return {
        date_from: formatDateLocal(startOfMonth),
        date_to: formatDateLocal(endOfMonth),
      };
    }
    case 'Last month': {
      const startOfLastMonth = new Date(
        today.getFullYear(),
        today.getMonth() - 1,
        1,
      );
      const endOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
      return {
        date_from: formatDateLocal(startOfLastMonth),
        date_to: formatDateLocal(endOfLastMonth),
      };
    }
    default:
      return {};
  }
};

export function OrderFilters({
  filters,
  onFiltersChange,
  status,
}: OrderFiltersProps) {
  const [activeStatus, setActiveStatus] = useState<OrderState | 'All'>(
    status || 'All',
  );
  const [activeTime, setActiveTime] = useState<TimeFilter>('This week');
  const [activeOrderType, setActiveOrderType] = useState<
    OrderTypeFilter | 'All'
  >('All');
  const [customDateRange, setCustomDateRange] = useState({
    from: '2000-01-01',
    to: '2099-12-31',
  });
  const [lotNumberValue, setLotNumberValue] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      onFiltersChange({
        ...filters,
        lot_number: lotNumberValue.trim() || undefined,
      });
    }, 500);

    return () => clearTimeout(timer);
  }, [lotNumberValue]);

  const visibleStatusFilters = status
    ? STATUS_FILTERS.filter((f) => f.value === status)
    : STATUS_FILTERS;

  const handleStatusChange = (newStatus: OrderState | 'All') => {
    setActiveStatus(newStatus);

    const finalState: OrderState | undefined =
      newStatus === 'All' ? undefined : newStatus;

    onFiltersChange({
      ...filters,
      state: finalState,
    });
  };

  const handleTimeChange = (time: TimeFilter) => {
    setActiveTime(time);
    if (time === 'Custom') {
      return;
    }

    if (time === 'Paid') {
      const threeDaysAgo = new Date();
      threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);
      const dateTo = formatDateLocal(threeDaysAgo);

      setActiveStatus('Paid');
      onFiltersChange({
        ...filters,
        state: 'Paid',
        date_to: dateTo,
        date_from: undefined,
        is_overdue_paid: true,
      });
      return;
    }

    const dateRange = getDateRange(time);
    onFiltersChange({
      ...filters,
      ...dateRange,
      is_overdue_paid: false,
    });
  };

  const handleOrderTypeChange = (orderType: OrderTypeFilter | 'All') => {
    setActiveOrderType(orderType);
    onFiltersChange({
      ...filters,
      relation_type: orderType === 'All' ? undefined : orderType,
    });
  };

  const handleCustomDateRangeChange = (from: string, to: string) => {
    setCustomDateRange({ from, to });
    setActiveTime('Custom');
    onFiltersChange({
      ...filters,
      date_from: from || undefined,
      date_to: to || undefined,
    });
  };

  return (
    <div className="space-y-4">
      {!status && (
        <div className="flex flex-wrap gap-2">
          {visibleStatusFilters.map((filter) => (
            <FilterOptionButton
              key={filter.value}
              label={filter.label}
              isActive={activeStatus === filter.value}
              onClick={() => handleStatusChange(filter.value)}
            />
          ))}
        </div>
      )}

      {!status && (
        <div className="flex flex-wrap items-center gap-2">
          {TIME_FILTERS.map((filter) => (
            <FilterOptionButton
              key={filter.value}
              label={filter.label}
              isActive={activeTime === filter.value}
              onClick={() => handleTimeChange(filter.value)}
            />
          ))}

          <DateRangePickerButton
            dateFrom={customDateRange.from}
            dateTo={customDateRange.to}
            onDateRangeChange={handleCustomDateRangeChange}
            isActive={activeTime === 'Custom'}
          />
        </div>
      )}

      {!status && (
        <div className="flex flex-wrap gap-2">
          {ORDER_TYPE_FILTERS.map((filter) => (
            <FilterOptionButton
              key={filter.value}
              label={filter.label}
              isActive={activeOrderType === filter.value}
              onClick={() => handleOrderTypeChange(filter.value)}
            />
          ))}
        </div>
      )}

      {!status && (
        <div className="flex w-56 gap-2">
          <Input
            type="text"
            placeholder="Search"
            label="Filter by LOT number"
            value={lotNumberValue}
            onChange={(e) => setLotNumberValue(e.target.value)}
          />
        </div>
      )}
    </div>
  );
}
